// packages/chat-domain/src/types.ts
//
// The chat domain entities. These mirror the `chat_*` tables (docs/chat-
// architecture.md §4) one-for-one, in app-facing shape: the repo (packages/
// chat-data) maps a Prisma row → one of these at the data boundary.
//
// Conventions:
// - IDs are `number`. The DB uses BIGINT for thread/message ids; the repo
//   `Number()`-coerces at the boundary (the established pattern for raw-query
//   ids, AGENTS.md "Prisma / dbBizData Queries"). Chat volumes are far below
//   2^53 for the foreseeable future.
// - Timestamps are ISO-8601 UTC strings (named `...AtUtc`, mirroring the
//   `DATETIME(0)` columns). The repo converts `Date` → ISO at the boundary.
//   (Calendar-day fields would use the `Ymd` brand, but a message instant is a
//   full datetime, for which the repo has no brand.)
// - Absence is `null`, never `''` (AGENTS.md "Date And Time Strings").

import type {
  ChatScope,
  ChatThreadStatus,
  ChatSenderType,
  ChatParticipantType,
  ChatClosedByType,
  ChatPresenceStatus,
  ChatChannelPlatform,
  ChatFeedbackCategory,
  ChatSpamFlaggedByType,
  ChatSpamFlagReason,
} from './enums';

/** A conversation: a stream of {@link ChatMessage}s with a polymorphic sender
 *  and a `status` that decides who drives. Mirrors `chat_threads`. */
export interface ChatThread {
  id: number;
  scope: ChatScope;
  /** Stable numeric join key into db-biz-data (NO cross-DB FK); null for an
   *  anonymous public-marketing chat. */
  bizId: number | null;
  /** Non-authoritative cached display label only; `bizId` is the join key. */
  bizSlug: string | null;
  /** db-biz-data `users.id` of the opener (null for anonymous); NO FK. */
  openedByBizUserId: number | null;
  /** When this thread arrived over a social channel (WhatsApp/Messenger/Instagram/
   *  Telegram) rather than the web widget, the platform; null for a web thread.
   *  Surfaced so the agent inbox can badge social chats by source. Mirrors
   *  `chat_threads.channelPlatform`. */
  channelPlatform: ChatChannelPlatform | null;
  subject: string | null;
  /** Non-null **iff** this thread is a product-feedback submission (the support
   *  widget's "Feedback" pill) — the category the user chose; null for a normal
   *  support/public chat. Mirrors `chat_threads.feedbackCategory`. */
  feedbackCategory: ChatFeedbackCategory | null;
  status: ChatThreadStatus;
  /** `admin_users.id` (db-core) for a support/public chat, or that business's
   *  own staff id for a `business_widget` chat. null while unassigned. */
  assignedAgentUserId: number | null;
  /** Per-thread monotonic seq high-water mark; the next message gets
   *  `lastMessageSeq + 1`, allocated under a row lock (see `ChatRepo`). */
  lastMessageSeq: number;
  lastMessageAtUtc: string | null;
  /** When the VISITOR (customer side) last wrote — stamped on inbound visitor
   *  messages, never on agent/AI/system ones. Feeds the Meta 24-hour customer-
   *  service reply window (`replyWindowState` in client/replyWindow.ts): a Meta
   *  channel only accepts an agent reply within 24 h of this instant. null when
   *  no visitor message has been stamped yet (pre-tracking threads included).
   *  Mirrors `chat_threads.lastVisitorMessageAtUtc`. */
  lastVisitorMessageAtUtc: string | null;
  closedAtUtc: string | null;
  closedByType: ChatClosedByType | null;
  closedByAgentUserId: number | null;
  /** Non-null **iff** this thread has been flagged as spam / a bot-loop — the
   *  STICKY flag: the AI concierge stays silent on it until an agent clears it
   *  ("Not spam"). Orthogonal to `status` (a flagged thread keeps its lifecycle
   *  state and a human can still take it over). See `evaluateBotTurnGuard`.
   *  Mirrors `chat_threads.spamFlaggedAtUtc`. */
  spamFlaggedAtUtc: string | null;
  /** Who flagged it: the auto-detector (`system`) or a human agent. */
  spamFlaggedByType: ChatSpamFlaggedByType | null;
  /** Why it was flagged (drives the console badge). */
  spamFlagReason: ChatSpamFlagReason | null;
  /** SOFT, self-healing cooldown: when set to a FUTURE instant, the bot is
   *  silent on this thread until then (a first-hit throttle a fast-typing human
   *  recovers through on its own); a loop that re-trips after it expires is
   *  promoted to the sticky `spamFlaggedAtUtc`. Mirrors
   *  `chat_threads.botCooldownUntilUtc`. */
  botCooldownUntilUtc: string | null;
  createdAtUtc: string;
}

/** A file attached to a message (v1: an annotated screenshot image). Mirrors a
 *  `chat_message_attachments` row. */
export interface ChatAttachment {
  id: number;
  kind: 'image';
  /** Resolved public media URL. */
  url: string;
  mimeType: string;
  width: number | null;
  height: number | null;
  byteSize: number | null;
}

/** One message in a thread. Mirrors `chat_messages`. The `body` is the single
 *  sanctioned free-form column (genuine user prose); files live in `attachments`. */
export interface ChatMessage {
  id: number;
  threadId: number;
  /** Per-thread monotonic order, server-assigned. Ordering is **always** by
   *  `threadSeq`, never by a client clock. `(threadId, threadSeq)` is unique. */
  threadSeq: number;
  /** Denormalized for fast tenant scans (mirrors `event_log_entries.bizId`). */
  bizId: number | null;
  senderType: ChatSenderType;
  /** `admin_users.id` when `senderType === 'tourtask_agent'`. */
  senderAgentUserId: number | null;
  /** db-biz-data `users.id` when `senderType === 'business_user'` (NO FK). */
  senderBizUserId: number | null;
  /** Client-generated UUID: at-least-once dedupe, optimistic-echo
   *  reconciliation, and idempotent retry. Unique per `(threadId, clientMsgId)`. */
  clientMsgId: string | null;
  body: string;
  /** Files attached to this message (empty for a plain text message). */
  attachments: ChatAttachment[];
  createdAtUtc: string;
}

/** A thread member + their read cursor. Mirrors `chat_participants`. Visitor
 *  fields carry anonymous public/embed PII (no user row anywhere). */
export interface ChatParticipant {
  id: number;
  threadId: number;
  participantType: ChatParticipantType;
  agentUserId: number | null;
  bizUserId: number | null;
  visitorName: string | null;
  visitorEmail: string | null;
  /** Local phone number, digits only (no calling code). */
  visitorPhone: string | null;
  /** ISO 3166-1 alpha-2 country code for the phone (CHAR(2)). */
  visitorPhoneCountryCode: string | null;
  /** Read-state cursor: messages with `id > lastReadMessageId` are unread. */
  lastReadMessageId: number | null;
  lastReadAtUtc: string | null;
  joinedAtUtc: string;
  leftAtUtc: string | null;
}

/** Durable mirror of an agent's presence. Mirrors `chat_agent_presence`. */
export interface ChatAgentPresence {
  agentUserId: number;
  status: ChatPresenceStatus;
  lastSeenAtUtc: string;
}

/**
 * A thread paired with its most-recent message — the read model behind the agent
 * inbox and the backoffice widget's past-conversation list. The dbCore-specific
 * query that produces it lives in `chat-data`; this is the browser-safe *shape*
 * so a client surface (the widget) can type the list without importing the
 * server-only persistence package.
 */
export interface ChatThreadWithLast {
  thread: ChatThread;
  lastMessage: ChatMessage | null;
}

/** A stable handle to a participant, for read-cursor and assignment ops where
 *  the caller knows a principal but not the `chat_participants.id`. */
export type ParticipantRef =
  | { kind: 'agent'; agentUserId: number }
  | { kind: 'business_user'; bizUserId: number }
  | { kind: 'participantId'; participantId: number };

/** Unread count for a participant = messages after their read cursor. */
export function unreadCount(messages: readonly ChatMessage[], lastReadMessageId: number | null): number {
  const cursor = lastReadMessageId ?? 0;
  let n = 0;
  for (const m of messages) if (m.id > cursor) n += 1;
  return n;
}

/**
 * Merge freshly-fetched messages into the current set, de-duped by `id` and
 * ordered by `threadSeq` — the client-side reconcile every chat surface runs
 * after a `fetchSince`/poll. Returns `prev` unchanged (same reference) when
 * nothing is new, so a React state updater bails out of a re-render.
 *
 * It also collapses an **optimistic echo** against its real server message:
 * among messages that share a non-null `clientMsgId`, only the one with the
 * highest `id` survives. A surface that shows a sent message immediately gives
 * the placeholder a temporary **negative** id, so the real (positive) server id
 * always wins once it arrives via the send response or the next poll. Two
 * genuine messages never share a `clientMsgId` (it is unique per thread), so a
 * real-only merge is unaffected and `null` clientMsgIds are never collapsed.
 */
export function mergeMessagesById(
  prev: ChatMessage[],
  incoming: readonly ChatMessage[],
): ChatMessage[] {
  if (incoming.length === 0) return prev;
  const byId = new Map<number, ChatMessage>();
  for (const m of prev) byId.set(m.id, m);
  for (const m of incoming) byId.set(m.id, m);

  // Pick the surviving id for each clientMsgId (highest id = the real message).
  let winnerByClientMsgId: Map<string, number> | null = null;
  for (const m of byId.values()) {
    if (m.clientMsgId == null) continue;
    if (!winnerByClientMsgId) winnerByClientMsgId = new Map();
    const best = winnerByClientMsgId.get(m.clientMsgId);
    if (best === undefined || m.id > best) winnerByClientMsgId.set(m.clientMsgId, m.id);
  }

  const merged: ChatMessage[] = [];
  for (const m of byId.values()) {
    if (m.clientMsgId != null && winnerByClientMsgId) {
      const winnerId = winnerByClientMsgId.get(m.clientMsgId);
      if (winnerId !== undefined && winnerId !== m.id) continue; // drop the optimistic placeholder
    }
    merged.push(m);
  }
  return merged.sort((a, b) => a.threadSeq - b.threadSeq);
}
